import type { WorkingTreeChangeKind } from '../types.js';
import { normalizeGitDiffPath } from '../parse/normalize-git-diff-path.js';
import { posixJoinDirFile } from '../paths/repo-format.js';
import type { NameStatusEntry, NumstatEntry } from '../parse/types.js';
import type { WorkingTreeChangeFileRow } from '../types.js';

export function buildChangedFileRow(options: {
  filePathRelativeToRepoRoot: string;
  repoPathRelativeToWorkspaceRoot: string;
  nameEntry?: NameStatusEntry;
  numEntry?: NumstatEntry;
  untracked?: boolean;
}): WorkingTreeChangeFileRow {
  const filePathRelativeToRepoRoot = normalizeGitDiffPath(options.filePathRelativeToRepoRoot);
  const filePathRelativeToWorkspaceRoot = posixJoinDirFile(
    options.repoPathRelativeToWorkspaceRoot,
    filePathRelativeToRepoRoot,
  );
  const additions = options.numEntry?.additions ?? 0;
  const deletions = options.numEntry?.deletions ?? 0;

  let change: WorkingTreeChangeKind = options.nameEntry?.change ?? 'modified';
  let movedFromFilePathRelativeToWorkspaceRoot: string | undefined;
  let movedFromFilePathRelativeToRepoRoot: string | undefined;

  const oldFilePathRelativeToRepoRoot =
    options.nameEntry?.oldPathInRepo ?? options.numEntry?.oldPathInRepo;
  if (change === 'moved' && oldFilePathRelativeToRepoRoot) {
    movedFromFilePathRelativeToRepoRoot = normalizeGitDiffPath(oldFilePathRelativeToRepoRoot);
    movedFromFilePathRelativeToWorkspaceRoot = posixJoinDirFile(
      options.repoPathRelativeToWorkspaceRoot,
      movedFromFilePathRelativeToRepoRoot,
    );
  } else if (oldFilePathRelativeToRepoRoot && change === 'modified') {
    change = 'moved';
    movedFromFilePathRelativeToRepoRoot = normalizeGitDiffPath(oldFilePathRelativeToRepoRoot);
    movedFromFilePathRelativeToWorkspaceRoot = posixJoinDirFile(
      options.repoPathRelativeToWorkspaceRoot,
      movedFromFilePathRelativeToRepoRoot,
    );
  }

  if (options.untracked && !options.nameEntry) {
    change = 'added';
  } else if (!options.nameEntry && options.numEntry && change !== 'moved') {
    if (additions > 0 && deletions === 0) change = 'added';
    else if (deletions > 0 && additions === 0) change = 'removed';
    else change = 'modified';
  }

  return {
    workspaceRelPath: filePathRelativeToWorkspaceRoot,
    additions,
    deletions,
    change,
    ...(movedFromFilePathRelativeToWorkspaceRoot
      ? { movedFromWorkspaceRelPath: movedFromFilePathRelativeToWorkspaceRoot }
      : {}),
    ...(movedFromFilePathRelativeToRepoRoot
      ? { movedFromPathInRepo: movedFromFilePathRelativeToRepoRoot }
      : {}),
  };
}
